<template>
  <v-dialog
    v-model="dialog"
    max-width="500"
    persistent
  >
    <v-card>
      <v-card-title class="text-h6">
        <font-awesome-icon
          :icon="['fas', 'bookmark']"
          class="mr-2 primary--text"
        />
        Save Current Filter
      </v-card-title>

      <v-card-text class="pb-0">
        <v-form
          ref="form"
          v-model="isValid"
        >
          <v-text-field
            v-model="filterName"
            label="Filter Name"
            :rules="[rules.required, rules.maxLength]"
            outlined
            dense
            autofocus
            counter="50"
            hint="Give your filter a memorable name"
            persistent-hint
            @keyup.enter="save"
          >
            <template v-slot:prepend-inner>
              <font-awesome-icon
                :icon="['fas', 'tag']"
                class="mr-1"
                size="sm"
              />
            </template>
          </v-text-field>

          <!-- Show current filter values as a preview -->
          <v-card
            v-if="hasActiveFilters"
            outlined
            class="mt-3 pa-3"
          >
            <div class="text-caption font-weight-bold mb-2 grey--text text--darken-1">
              Filter Preview:
            </div>
            <v-chip-group column>
              <v-chip
                v-for="(filter, index) in activeFiltersDisplay"
                :key="index"
                x-small
                outlined
                color="primary"
              >
                {{ filter }}
              </v-chip>
            </v-chip-group>
          </v-card>

          <v-alert
            v-else
            type="info"
            text
            dense
            class="mt-3"
          >
            <div class="text-caption">
              No filters are currently active. Apply filters in the view before saving.
            </div>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!isValid || !hasActiveFilters"
          :loading="saving"
          @click="save"
        >
          <font-awesome-icon
            :icon="['fas', 'check']"
            class="mr-2"
            size="sm"
          />
          Save Filter
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SaveFilterDialog',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    viewName: {
      type: String,
      required: true,
    },
    currentFilters: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      filterName: '',
      isValid: false,
      saving: false,
      rules: {
        required: (v) => !!v || 'Filter name is required',
        maxLength: (v) => (v && v.length <= 50) || 'Name must be 50 characters or less',
      },
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    hasActiveFilters() {
      // Check if any filters are actually set
      const { currentFilters } = this
      if (!currentFilters) return false

      // Check for various filter types
      const hasPersonFilter = Array.isArray(currentFilters.selectedPersonFilter)
        && currentFilters.selectedPersonFilter.length > 0
      const hasTimeTypeFilter = !!currentFilters.selectedTimeTypeFilter
      const hasClientFilter = !!currentFilters.selectedClientFilter
      const hasProjectFilter = !!currentFilters.selectedProjectFilter
      const hasSearch = !!currentFilters.tableSearch && currentFilters.tableSearch.trim() !== ''
      const hasDateRange = currentFilters.dateRange && currentFilters.dateRange.length === 2

      return hasPersonFilter || hasTimeTypeFilter || hasClientFilter
        || hasProjectFilter || hasSearch || hasDateRange
    },

    activeFiltersDisplay() {
      const filters = []
      const { currentFilters } = this

      if (!currentFilters) return filters

      // Person filter
      if (Array.isArray(currentFilters.selectedPersonFilter)
        && currentFilters.selectedPersonFilter.length > 0) {
        filters.push(`${currentFilters.selectedPersonFilter.length} person(s)`)
      }

      // Time type filter
      if (currentFilters.selectedTimeTypeFilter) {
        filters.push(`Type: ${currentFilters.selectedTimeTypeFilter}`)
      }

      // Client filter
      if (currentFilters.selectedClientFilter) {
        filters.push(`Client: ${currentFilters.selectedClientFilter}`)
      }

      // Project filter
      if (currentFilters.selectedProjectFilter) {
        filters.push(`Project: ${currentFilters.selectedProjectFilter}`)
      }

      // Search filter
      if (currentFilters.tableSearch && currentFilters.tableSearch.trim() !== '') {
        filters.push(`Search: "${currentFilters.tableSearch}"`)
      }

      // Date range
      if (currentFilters.dateRange && currentFilters.dateRange.length === 2) {
        filters.push(`Date: ${currentFilters.dateRange[0]} to ${currentFilters.dateRange[1]}`)
      }

      return filters
    },
  },

  watch: {
    dialog(newVal) {
      if (newVal) {
        // Reset form when dialog opens
        this.filterName = ''
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.resetValidation()
          }
        })
      }
    },
  },

  methods: {
    async save() {
      if (!this.$refs.form.validate()) {
        return
      }

      if (!this.hasActiveFilters) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'No filters to save. Apply filters before saving.',
          status: 'warning',
        }, { root: true })
        return
      }

      this.saving = true

      try {
        // Create new filter
        await this.$store.dispatch('savedFilters/saveFilter', {
          name: this.filterName,
          view: this.viewName,
          filters: this.currentFilters,
        })

        this.dialog = false
        this.filterName = ''
      } catch (error) {
        // Error handled by store action
      } finally {
        this.saving = false
      }
    },

    cancel() {
      this.dialog = false
      this.filterName = ''
    },
  },
}
</script>

<style scoped lang="scss">
.v-chip-group {
  gap: 4px;
}
</style>
